import { memo, type ReactNode } from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from './Button.js';

export type SettingsProgressIncompleteKind = 'recommended' | 'needs-setup';

export interface SettingsProgressCompletedItem {
  readonly id: string;
  readonly label: string;
}

export interface SettingsProgressIncompleteItem {
  readonly id: string;
  readonly label: string;
  readonly kind: SettingsProgressIncompleteKind;
  /** Nest-under-rollup child gate labels (e.g. productPaymentCatalog sub-gates). */
  readonly nestedLabels?: readonly string[];
  /** Clickable nested checklist rows (id + label). Takes precedence over nestedLabels when set. */
  readonly nestedItems?: readonly SettingsProgressNestedItem[];
}

export interface SettingsProgressNestedItem {
  readonly id: string;
  readonly label: string;
}

export interface SettingsConfigurationProgressCardProps {
  readonly title: string;
  readonly percent: number;
  readonly completedItems: readonly SettingsProgressCompletedItem[];
  readonly incompleteItems: readonly SettingsProgressIncompleteItem[];
  readonly status: string;
  readonly className?: string;
  /** Overrides root `data-testid` (default: settings-configuration-progress-card). */
  readonly dataTestId?: string;
  /** Shown only when percent is 100. Hides the card after confirmed dismiss. */
  readonly dismissLabel?: string;
  readonly onDismiss?: () => void;
  /** Disables dismiss while confirmed PUT is in flight (G1). */
  readonly dismissBusy?: boolean;
  /** Jump to the settings section for a checklist item (complete or incomplete). */
  readonly onJumpToIncomplete?: (itemId: string) => void;
  /** Jump to a nested checklist row under an incomplete rollup item. */
  readonly onJumpToNested?: (parentItemId: string, nestedItemId: string) => void;
  /**
   * When set, Continue and row jumps skip ids that return false (status-only rows).
   * Omitted → every item with `onJumpToIncomplete` is jumpable (legacy callers).
   */
  readonly canJumpItem?: (itemId: string) => boolean;
  readonly jumpLabel?: string;
}

function renderIncompleteNestedList(
  item: SettingsProgressIncompleteItem,
  canJumpNested: boolean,
  onJumpToNested?: (parentItemId: string, nestedItemId: string) => void,
): ReactNode {
  if (item.nestedItems != null && item.nestedItems.length > 0) {
    return (
      <ul
        className="asp-settings-progress-card__nested-list"
        data-testid={`settings-configuration-progress-nested-${item.id}`}
      >
        {item.nestedItems.map((nestedItem) => (
          <li key={nestedItem.id} className="asp-settings-progress-card__nested-row">
            {canJumpNested ? (
              <button
                type="button"
                className="asp-settings-progress-card__jump asp-settings-progress-card__jump--nested"
                data-testid={`settings-configuration-progress-jump-${item.id}-${nestedItem.id}`}
                onClick={() => {
                  onJumpToNested?.(item.id, nestedItem.id);
                }}
              >
                {nestedItem.label}
              </button>
            ) : (
              nestedItem.label
            )}
          </li>
        ))}
      </ul>
    );
  }
  if (item.nestedLabels != null && item.nestedLabels.length > 0) {
    return (
      <ul
        className="asp-settings-progress-card__nested-list"
        data-testid={`settings-configuration-progress-nested-${item.id}`}
      >
        {item.nestedLabels.map((nestedLabel) => (
          <li key={nestedLabel} className="asp-settings-progress-card__nested-row">
            {nestedLabel}
          </li>
        ))}
      </ul>
    );
  }
  return null;
}

function hasIncompleteNestedContent(item: SettingsProgressIncompleteItem): boolean {
  return (
    (item.nestedItems != null && item.nestedItems.length > 0) ||
    (item.nestedLabels != null && item.nestedLabels.length > 0)
  );
}

export const SettingsConfigurationProgressCard = memo(
  ({
    title,
    percent,
    completedItems,
    incompleteItems,
    status,
    className = '',
    dataTestId = 'settings-configuration-progress-card',
    dismissLabel,
    onDismiss,
    dismissBusy = false,
    onJumpToIncomplete,
    onJumpToNested,
    canJumpItem,
    jumpLabel,
  }: SettingsConfigurationProgressCardProps): JSX.Element => {
    const rootClass = ['asp-settings-progress-card', className].filter((part) => part.length > 0).join(' ');
    const canDismiss = percent >= 100 && onDismiss != null && dismissLabel != null && dismissLabel.length > 0;
    const itemIsJumpable = (itemId: string): boolean =>
      itemId.length > 0 && (canJumpItem == null || canJumpItem(itemId));
    const firstJumpableIncompleteId =
      incompleteItems.find((item) => itemIsJumpable(item.id))?.id ?? null;
    const canJump = onJumpToIncomplete != null && firstJumpableIncompleteId != null;
    const canJumpAny = onJumpToIncomplete != null;
    const canJumpNested = onJumpToNested != null;

    return (
      <section className={rootClass} data-testid={dataTestId} aria-live="polite">
        <header className="asp-settings-progress-card__head">
          <h2 className="asp-settings-progress-card__title">{title}</h2>
          <p className="asp-settings-progress-card__percent">{`${percent} %`}</p>
        </header>
        <p className="asp-settings-progress-card__status">{status}</p>

        {completedItems.length > 0 || incompleteItems.length > 0 ? (
          <ul className="asp-settings-progress-card__list">
            {completedItems.map((item) => {
              const rowJumpable = canJumpAny && itemIsJumpable(item.id);
              return (
                <li key={`done-${item.id}`} className="asp-settings-progress-card__row">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-[var(--color-an-success)] stroke-[1.75]" aria-hidden="true" />
                  {rowJumpable ? (
                    <button
                      type="button"
                      className="asp-settings-progress-card__jump"
                      data-testid={`settings-configuration-progress-jump-${item.id}`}
                      onClick={() => {
                        onJumpToIncomplete?.(item.id);
                      }}
                    >
                      {item.label}
                    </button>
                  ) : (
                    <span data-testid={`settings-configuration-progress-status-${item.id}`}>{item.label}</span>
                  )}
                </li>
              );
            })}
            {incompleteItems.map((item) => {
              const rowJumpable = canJumpAny && itemIsJumpable(item.id);
              return (
                <li
                  key={item.id}
                  className={
                    hasIncompleteNestedContent(item)
                      ? 'asp-settings-progress-card__row asp-settings-progress-card__row--nested'
                      : 'asp-settings-progress-card__row'
                  }
                >
                  <AlertTriangle
                    className={
                      item.kind === 'needs-setup'
                        ? 'h-4 w-4 shrink-0 text-[var(--color-an-warn)] stroke-[1.75]'
                        : 'h-4 w-4 shrink-0 text-[var(--color-an-primary)] stroke-[1.75]'
                    }
                    aria-hidden="true"
                  />
                  <div className="flex min-w-0 flex-col gap-0.5">
                    {rowJumpable ? (
                      <button
                        type="button"
                        className="asp-settings-progress-card__jump"
                        data-testid={`settings-configuration-progress-jump-${item.id}`}
                        onClick={() => {
                          onJumpToIncomplete?.(item.id);
                        }}
                      >
                        {item.label}
                      </button>
                    ) : (
                      <span data-testid={`settings-configuration-progress-status-${item.id}`}>
                        {item.label}
                      </span>
                    )}
                    {renderIncompleteNestedList(item, canJumpNested, onJumpToNested)}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}

        {canJump || canDismiss ? (
          <div className="asp-settings-progress-card__actions">
            {canJump ? (
              <Button
                type="button"
                intent="primary"
                size="sm"
                onClick={() => {
                  if (firstJumpableIncompleteId != null) {
                    onJumpToIncomplete(firstJumpableIncompleteId);
                  }
                }}
                data-testid="settings-configuration-progress-jump-first"
              >
                {jumpLabel ?? 'Continue setup'}
              </Button>
            ) : null}
            {canDismiss ? (
              <Button
                type="button"
                intent="secondary"
                size="sm"
                loading={dismissBusy}
                onClick={onDismiss}
                data-testid="settings-configuration-progress-dismiss"
              >
                {dismissLabel}
              </Button>
            ) : null}
          </div>
        ) : null}
      </section>
    );
  },
);

SettingsConfigurationProgressCard.displayName = 'SettingsConfigurationProgressCard';
